import React, {
  forwardRef,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { PrepTimeIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import { format } from 'date-fns'
import parseISO from 'date-fns/parseISO'
import sortBy from 'lodash/sortBy'

// eslint-disable-next-line camelcase
import {
  DateTimeRange,
  Menus_MenuItemTag,
  TimeBasedRules,
  DayOfWeek
} from 'src/apollo/onlineOrdering'
import { DisplayableMenuItemTag } from 'src/apollo/sites'
import {
  FulfillmentData,
  getDateTimeIndexes,
  useFulfillment
} from 'src/public/components/online_ordering/FulfillmentContext'
import {
  dayStrs,
  getPickupWindowRuleDuration
} from 'src/public/components/online_ordering/pickupWindowUtils'

import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import {
  ScreenWidth,
  useIsMobile,
  useScreenWidth
} from 'shared/js/utils/WindowContext'

import { MenuItem } from 'public/components/default_template/menu_section/MenuSection'
import { shouldApplyOutOfStockStyles } from 'public/components/default_template/menu_section/MenuItemCard'
import { DEFAULT_COLORS } from 'public/components/default_template/meta/StyleMeta'
import { TagIcon } from 'public/components/default_template/offers/OffersIcons'
import {
  CalendarIcon,
  ChefsHatIcon
} from 'public/components/default_template/online_ordering/item_tags/ItemTagIcons'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useOptionalTimeBasedRules } from 'public/components/online_ordering/TimeBasedRuleContext'

import {
  generateTextColor,
  getTagColor,
  Hex,
  isContrastCompliant
} from './tagColorUtils'

import { t } from 'src/translations'

export const MenuItemTags = ({
  item,
  backgroundColor,
  numOffers
}: {
  item: MenuItem
  backgroundColor?: string
  numOffers?: number
}) => {
  const { channel } = useCart()
  const {
    restaurant: { config }
  } = useRestaurant()
  const { timeBasedRulesMap } = useOptionalTimeBasedRules() || {
    timeBasedRulesMap: {} as NonNullable<
      ReturnType<typeof useOptionalTimeBasedRules>
    >['timeBasedRulesMap']
  }
  const timeBasedRules = item.guid && timeBasedRulesMap[item.guid]
  const tbrTag = useMemo(
    () =>
      timeBasedRules ? (
        <TimeBasedRuleMenuItemTag
          timeBasedRules={timeBasedRules}
          backgroundColor={backgroundColor}
          testId={'time-based-ordering-tag-' + item.guid}
        />
      ) : undefined,
    [timeBasedRules, item.guid, backgroundColor]
  )
  const offerBadge = useMemo(
    () =>
      numOffers && numOffers > 0 ? (
        <OfferBadge
          text={
            numOffers > 1
              ? t('menu-item-tags.offers')
              : t('menu-item-tags.offer')
          }
        />
      ) : undefined,
    [numOffers]
  )
  const isMobile = useIsMobile(ScreenWidth.EXTRA_SMALL)
  const mobileOptimizationEnabled = config.ooConfig?.format?.mobileOptimization
  const offerBadgeShouldBeInline = !!(
    mobileOptimizationEnabled &&
    isMobile &&
    offerBadge
  )

  // Regardless of the state of the FF, TBR tags, or any menu item tags, if the item should show out of stock styles, that's all we'll return
  if (shouldApplyOutOfStockStyles(item, channel)) {
    return (
      <div className='itemTagsCardContainer' data-testid='menu-item-tags'>
        <OutOfStockTag />
      </div>
    )
  }

  const itemTags = item.itemTags ?? []

  if (itemTags.length === 0 && !tbrTag && !offerBadge) {
    return null
  }

  return (
    <div className='itemTagsCardContainer' data-testid='menu-item-tags'>
      {/* Offer badge is rendered separately unless inline mode is active */}
      {!offerBadgeShouldBeInline && offerBadge}
      {(itemTags.length > 0 || tbrTag || offerBadgeShouldBeInline) && (
        <DynamicMenuItemTags
          itemTags={itemTags}
          backgroundColor={backgroundColor}
          tbrTag={tbrTag}
          offerBadge={offerBadgeShouldBeInline ? offerBadge : undefined}
        />
      )}
    </div>
  )
}

// eslint-disable-next-line camelcase
export const filterAndSortTags = (
  itemTags: Menus_MenuItemTag[],
  displayableTags: DisplayableMenuItemTag[],
  allMenuItemTags?: Menus_MenuItemTag[]
) => {
  const sortOrderGuids = sortBy(displayableTags, 'displayOrder').map(
    (tag) => tag.menuItemTagId
  )

  // Build GUID -> name mapping from ALL menu item tags (not just this item's tags)
  // This is critical for RF-32427: when item has GUID-B but config references GUID-A,
  // we need to look up GUID-A from other items to learn the tag name
  const allTags = allMenuItemTags ?? itemTags ?? []
  const guidToName = new Map(
    allTags
      .filter((tag) => tag.guid && tag.name)
      .map((tag) => [tag.guid, tag.name])
  )

  // Build array of displayable tag names by looking up GUIDs
  const sortOrderNames = sortBy(displayableTags, 'displayOrder')
    .map((tag) => guidToName.get(tag.menuItemTagId))
    .filter((name): name is string => !!name)

  return sortBy(
    itemTags?.filter(
      (tag) =>
        (tag && tag.guid && sortOrderGuids.includes(tag.guid)) ||
        (tag && tag.name && sortOrderNames.includes(tag.name))
    ) ?? [],
    (tag) => {
      const guidIndex = sortOrderGuids.indexOf(tag.guid)
      const nameIndex = sortOrderNames.indexOf(tag.name ?? '')
      return guidIndex !== -1 ? guidIndex : nameIndex
    }
  )
}

// eslint-disable-next-line camelcase
const DynamicMenuItemTags = ({
  itemTags,
  backgroundColor,
  tbrTag,
  offerBadge
}: {
  itemTags: Menus_MenuItemTag[]
  backgroundColor?: string
  tbrTag?: ReactNode
  offerBadge?: ReactNode
}) => {
  const {
    restaurant: {
      meta: { primaryColor, theme }
    }
  } = useRestaurant()
  const tagsContainerRef = useRef<HTMLDivElement>(null)
  const firstTagRef = useRef<HTMLDivElement>(null)
  const secondTagRef = useRef<HTMLDivElement>(null)
  const thirdTagRef = useRef<HTMLDivElement>(null)
  const plusMoreRef = useRef<HTMLDivElement>(null)

  const [firstTagFits, setFirstTagFits] = useState(false)
  const [secondTagFits, setSecondTagFits] = useState(false)
  const [thirdTagFits, setThirdTagFits] = useState(false)
  const [firstTagWidth, setFirstTagWidth] = useState<number>()
  const [secondTagWidth, setSecondTagWidth] = useState<number>()
  const [thirdTagWidth, setThirdTagWidth] = useState<number>()
  const [twoTagWidth, setTwoTagWidth] = useState<number>()
  const [threeTagWidth, setThreeTagWidth] = useState<number>()

  const [plusMore, setPlusMore] = useState(0)
  const [firstRender, setFirstRender] = useState(true)
  const screenWidth = useScreenWidth()

  /**
   * Calculate total number of tags to account for in the dynamic rendering logic.
   * This includes:
   * - All itemTags (menu item tags)
   * - Time Based Rule tag (if present)
   * - Offer Badge (if present and should be inline based on mobile optimization config)
   */
  const orderedTags = [tbrTag, offerBadge, ...itemTags].filter((tag) => !!tag)
  const numTags = orderedTags.length

  useEffect(() => {
    // Calculate how much width each tag would take up if we were to render them so that we can decide how many to ultimately render
    const firstWidth = firstTagRef.current?.getBoundingClientRect().width
    const secondWidth = secondTagRef.current?.getBoundingClientRect().width
    const thirdWidth = thirdTagRef.current?.getBoundingClientRect().width
    setFirstTagWidth(firstWidth)
    setSecondTagWidth(secondWidth)
    setThirdTagWidth(thirdWidth)
    setTwoTagWidth(
      firstWidth && secondWidth ? firstWidth + 8 + secondWidth : undefined
    )
    setThreeTagWidth(
      firstWidth && secondWidth && thirdWidth
        ? firstWidth + 8 + secondWidth + 8 + thirdWidth
        : undefined
    )
    setFirstRender(false)
  }, [])

  useEffect(() => {
    if (!tagsContainerRef.current) {
      return
    }
    const containerWidth =
      tagsContainerRef.current.getBoundingClientRect().width
    const firstFits = !!(firstTagWidth && firstTagWidth < containerWidth)
    const secondFits = !!(twoTagWidth && twoTagWidth < containerWidth)
    const thirdFits = !!(threeTagWidth && threeTagWidth < containerWidth)
    let plusMore = 0
    if (numTags > 0 && !firstFits) {
      // Not even the first tag fits
      plusMore = numTags
    } else if (numTags > 1 && !secondFits) {
      // Show first tag plus however many don't fit
      plusMore = numTags - 1
    } else if (numTags > 2 && secondFits && !thirdFits) {
      // Show first two tags plus however many don't fit
      plusMore = numTags - 2
    } else if (numTags > 3 && thirdFits) {
      plusMore = numTags - 3
    }
    setFirstTagFits(firstFits)
    setSecondTagFits(secondFits)
    setThirdTagFits(thirdFits)

    if (plusMore === 0) {
      setPlusMore(plusMore)
      return
    }
    // Make sure the plusMore tag will fit, and move a tag into a hidden state while increasing plusMore count if not
    const plusMoreWidth =
      plusMoreRef.current?.getBoundingClientRect().width ?? 0
    const combinedWidth = firstTagWidth ? plusMoreWidth + firstTagWidth + 8 : 0

    // Just show the (+{count}) tag since none fit
    if (combinedWidth >= containerWidth && itemTags.length > 0) {
      setFirstTagFits(false)
      plusMore = numTags
    } else if (
      secondFits &&
      !thirdFits &&
      twoTagWidth + 8 + plusMoreWidth > containerWidth
    ) {
      // Shows the first tag and then the (+{count}) tag
      setSecondTagFits(false)
      plusMore += 1
    } else if (
      thirdFits &&
      threeTagWidth + 8 + plusMoreWidth > containerWidth
    ) {
      // Shows the first two tags and then the (+{count}) tag
      setThirdTagFits(false)
      plusMore += 1
    }
    setPlusMore(plusMore)
  }, [
    screenWidth,
    firstTagWidth,
    secondTagWidth,
    thirdTagWidth,
    twoTagWidth,
    threeTagWidth,
    tagsContainerRef,
    numTags,
    setPlusMore,
    itemTags.length
  ])
  const [firstSlot, secondSlot, thirdSlot] = orderedTags

  const background = backgroundColor ?? DEFAULT_COLORS.background ?? '#FFFFFF'
  const primary = primaryColor ?? DEFAULT_COLORS.primary ?? '#000000'
  const fallbackTagColor = getTagColor(background, primary)
  const fallbackTextColor = generateTextColor(background as Hex)
  const themeEnabled = theme?.enabled
  const tagColor = themeEnabled
    ? theme.colorScheme.border.default
    : fallbackTagColor
  const textColor = themeEnabled
    ? theme.colorScheme.text.default
    : fallbackTextColor

  // Helper to check if a slot contains a menu item tag (vs special tag like tbrTag/offerBadge)
  const isMenuItemTag = (
    slot: ReactNode | Menus_MenuItemTag | undefined
  ): slot is Menus_MenuItemTag =>
    !!slot &&
    typeof slot === 'object' &&
    '__typename' in slot &&
    slot.__typename === 'Menus_MenuItemTag'

  // Helper to render a tag slot
  const renderSlot = (
    slot: ReactNode | Menus_MenuItemTag | undefined,
    shouldRender: boolean,
    ref: React.RefObject<HTMLDivElement>
  ) => {
    if (!slot || !(firstRender || shouldRender)) return null

    return (
      <div ref={ref}>
        {isMenuItemTag(slot) && slot.name ? (
          <MenuItemTag
            tagColor={tagColor}
            textColor={textColor}
            subtle
            title={slot.name.length > 25 ? slot.name : undefined}
          >
            {getDisplayName(slot.name)}
          </MenuItemTag>
        ) : (
          (slot as ReactNode)
        )}
      </div>
    )
  }

  return (
    <div className='dynamicTagContainer' ref={tagsContainerRef}>
      {renderSlot(firstSlot, firstTagFits, firstTagRef)}
      {renderSlot(secondSlot, secondTagFits, secondTagRef)}
      {renderSlot(thirdSlot, thirdTagFits, thirdTagRef)}
      {/* Plus more indicator */}
      {plusMore > 0 && (
        <MenuItemTag
          ref={plusMoreRef}
          tagColor={tagColor}
          textColor={textColor}
          subtle
        >
          +{plusMore}
        </MenuItemTag>
      )}
    </div>
  )
}

const getDisplayName = (name: string) => {
  return (name.length > 25 ? name.substring(0, 25) + '...' : name).toUpperCase()
}

// eslint-disable-next-line react/display-name
export const MenuItemTag = forwardRef(
  (
    {
      tagColor,
      textColor,
      subtle,
      testId,
      Icon,
      className,
      title,
      children
    }: {
      tagColor: string
      textColor?: string
      subtle?: boolean
      testId?: string
      Icon?: React.FC<any>
      className?: string
      title?: string
      ref?: RefObject<HTMLDivElement>
      children: ReactNode
    },
    ref: React.RefObject<HTMLDivElement>
  ) => {
    const style = subtle
      ? { border: `1px solid ${tagColor}`, color: textColor }
      : { backgroundColor: tagColor, color: textColor }

    return (
      <div
        ref={ref}
        className={classnames('itemTag', className, { subtle })}
        style={style}
        title={title}
        data-testid={testId}
      >
        {Icon && <Icon color={textColor} />}
        {children}
      </div>
    )
  }
)

export const OutOfStockTag = () => {
  return (
    <MenuItemTag tagColor='#ffcdc7' textColor='#ba1a1a'>
      {t('menu-item-tags.out-of-stock')}
    </MenuItemTag>
  )
}

export const OfferBadge = ({
  text,
  className
}: {
  text: string
  className?: string
}) => {
  const { restaurant } = useRestaurant()
  const tagColor =
    restaurant.ooConfig?.promoCodeConfig?.backgroundColor ??
    restaurant.meta.primaryColor ??
    DEFAULT_COLORS.primary
  // Prefer white text over black when contrast is sufficient
  const textColor = isContrastCompliant('#FFFFFF', tagColor)
    ? '#FFFFFF'
    : '#000000'

  return (
    <MenuItemTag
      tagColor={tagColor}
      textColor={textColor}
      Icon={TagIcon}
      className={className}
      testId='offer-badge'
    >
      {text}
    </MenuItemTag>
  )
}

export const TimeBasedRuleItemModalTag = ({
  timeBasedRules,
  fulfillmentTimeValid,
  testId
}: {
  timeBasedRules: TimeBasedRules
  fulfillmentTimeValid: boolean
  testId?: string
}) => {
  const { fulfillmentData } = useFulfillment()
  const Icon = getIcon(timeBasedRules)
  let message = timeBasedRules.pickupWindowRule
    ? t('menu-item-tags.adding-this-item-may')
    : t('menu-item-tags.adding-this-item-will')
  const content = getTBRContent(timeBasedRules, false, fulfillmentData)

  return (
    <div className='tbrContent' data-testid={testId}>
      <div
        className={classnames('tbrHeader', {
          hasFulfillmentMessage: !fulfillmentTimeValid
        })}
      >
        {Icon && <Icon color='#252525' />}
        {content}
      </div>
      {!fulfillmentTimeValid && (
        <div className='fulfillmentMessage'>{message}</div>
      )}
    </div>
  )
}

export const TimeBasedRuleMenuItemTag = ({
  timeBasedRules,
  testId,
  backgroundColor,
  tagColorOverride
}: {
  timeBasedRules: TimeBasedRules
  testId?: string
  backgroundColor?: string
  tagColorOverride?: string
}) => {
  const { fulfillmentData } = useFulfillment()
  const {
    restaurant: {
      meta: { primaryColor, theme }
    }
  } = useRestaurant()
  const background = backgroundColor ?? DEFAULT_COLORS.background ?? '#FFFFFF'
  const primary = primaryColor ?? DEFAULT_COLORS.primary ?? '#000000'
  const fallbackTagColor = getTagColor(background, primary)
  const fallbackTextColor = generateTextColor(background as Hex)
  const themeEnabled = theme?.enabled
  const tagColor =
    tagColorOverride ??
    (themeEnabled ? theme.colorScheme.border.default : fallbackTagColor)
  const textColor = themeEnabled
    ? theme.colorScheme.text.default
    : fallbackTextColor
  const content = getTBRContent(timeBasedRules, true, fulfillmentData)

  return (
    <MenuItemTag
      Icon={getIcon(timeBasedRules)}
      tagColor={tagColor}
      textColor={textColor}
      testId={testId}
      subtle
    >
      {content}
    </MenuItemTag>
  )
}

export const getTBRContent = (
  timeBasedRules: TimeBasedRules,
  isTag: boolean,
  fulfillmentData?: FulfillmentData
) => {
  if (timeBasedRules.leadTimeRule) {
    const leadTime = timeBasedRules.leadTimeRule.leadTime
    return getLeadTimeForTag(leadTime, isTag)
  } else if (timeBasedRules.preorderRule) {
    const { start, end } = timeBasedRules.preorderRule
      .fulfillmentDateRange as DateTimeRange
    const dateRange = getDateRangeForTag(
      new Date(parseISO(start)),
      end ? new Date(parseISO(end)) : undefined,
      isTag
    )
    return isTag ? dateRange : t('menu-item-tags.preorder-today', { dateRange })
  } else if (timeBasedRules.pickupWindowRule) {
    const duration = getPickupWindowRuleDuration(
      timeBasedRules.pickupWindowRule,
      dayStrs[new Date().getDay()] || DayOfWeek.Monday
    )
    const [dayIndex] = getDateTimeIndexes(
      fulfillmentData?.cartFulfillmentData.fulfillmentDateTime,
      fulfillmentData?.scheduleData.futureScheduleDates
    )
    const closingTimeOnSelectedDay =
      fulfillmentData?.scheduleData.futureScheduleDates[
        dayIndex || 0
      ]?.times.at(-1)?.time
    return generatePickupWindowTagContent(
      duration,
      isTag,
      closingTimeOnSelectedDay
    )
  }
  return ''
}

export const getLeadTimeForTag = (leadTime: number, isTag: boolean) => {
  if (leadTime <= 48) {
    return t(`menu-item-tags.${isTag ? '' : 'requires-'}hours-notice`, {
      hours: leadTime
    })
  }
  // If lead time is greater than 48 hours, express in terms of days (and hours)
  const days = Math.floor(leadTime / 24)
  const hours = leadTime % 24
  if (hours === 0) {
    return t(`menu-item-tags.${isTag ? '' : 'requires-'}days-notice`, { days })
  }
  if (hours === 1 && !isTag) {
    return t('menu-item-tags.requires-days-hour-notice', { days })
  }
  return t(`menu-item-tags.${isTag ? '' : 'requires-'}days-hours-notice`, {
    days,
    hours
  })
}

export const getDateRangeForTag = (
  start: Date,
  end?: Date,
  upperCase?: boolean
) => {
  let startMonthAndDay = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
  if (upperCase) startMonthAndDay = startMonthAndDay.toUpperCase()
  // The front end of the TBR admin spa currently enforces an end time on all ranges, so this should never actually
  // be undefined, but in case it somehow is, we'll treat it as an open-ended range
  if (
    !end ||
    (start.getMonth() === end.getMonth() && start.getDate() === end.getDate())
  ) {
    return startMonthAndDay
  }
  if (start.getMonth() === end.getMonth()) {
    let endString = end.toLocaleDateString('en-US', { day: 'numeric' })
    if (upperCase) endString = endString.toUpperCase()
    return `${startMonthAndDay} - ${endString}`
  }
  let endString = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  })
  if (upperCase) endString = endString.toUpperCase()
  return `${startMonthAndDay} - ${endString}`
}

export const generatePickupWindowTagContent = (
  duration: number,
  isTag: boolean,
  closingTime?: string
) => {
  if (closingTime) {
    const endTime = parseISO(closingTime)
    const startTime = new Date(endTime.getTime() - duration * 60 * 60 * 1000)

    const endtimeFormatted = format(endTime, 'h:mm a')
    const startTimeFormatted = format(startTime, 'h:mm a')

    return isTag
      ? t('menu-item-tags.pickup', { startTimeFormatted, endtimeFormatted })
      : t('menu-item-tags.order-pickup-between', {
          startTimeFormatted,
          endtimeFormatted
        })
  }

  return isTag
    ? duration === 1
      ? t('menu-item-tags.pickup-before-close-singular', { duration })
      : t('menu-item-tags.pickup-before-close-plural', { duration })
    : duration === 1
    ? t('menu-item-tags.order-pickup-within-singular', { duration })
    : t('menu-item-tags.order-pickup-within-plural', { duration })
}

export const getIcon = (timeBasedRules: TimeBasedRules) => {
  if (timeBasedRules.leadTimeRule) {
    return ChefsHatIcon
  } else if (timeBasedRules.pickupWindowRule) {
    return PrepTimeIcon
  }
  return CalendarIcon
}
